import AsyncStorage from '@react-native-async-storage/async-storage';
import type { PrototypeSession } from '../types/prototypeSession';

const PROTOTYPE_SESSION_KEY = '@dengfeng/prototype-session';

export const defaultPrototypeSession: PrototypeSession = {
  isLoggedIn: false,
  onboardingComplete: false,
  gradeLabel: null,
  updatedAt: null,
};

const withUpdatedAt = (session: Omit<PrototypeSession, 'updatedAt'>): PrototypeSession => ({
  ...session,
  updatedAt: new Date().toISOString(),
});

const normalizePrototypeSession = (value: unknown): PrototypeSession => {
  if (!value || typeof value !== 'object') {
    return defaultPrototypeSession;
  }

  const session = value as Partial<PrototypeSession>;
  return {
    isLoggedIn: session.isLoggedIn === true,
    onboardingComplete: session.onboardingComplete === true,
    gradeLabel: typeof session.gradeLabel === 'string' ? session.gradeLabel : null,
    updatedAt: typeof session.updatedAt === 'string' ? session.updatedAt : null,
  };
};

export const getPrototypeSession = async (): Promise<PrototypeSession> => {
  try {
    const rawSession = await AsyncStorage.getItem(PROTOTYPE_SESSION_KEY);
    if (!rawSession) {
      return defaultPrototypeSession;
    }
    return normalizePrototypeSession(JSON.parse(rawSession));
  } catch {
    return defaultPrototypeSession;
  }
};

export const savePrototypeSession = async (
  session: PrototypeSession
): Promise<PrototypeSession> => {
  await AsyncStorage.setItem(PROTOTYPE_SESSION_KEY, JSON.stringify(session));
  return session;
};

export const markPrototypeLoggedIn = async (): Promise<PrototypeSession> => {
  const current = await getPrototypeSession();
  return savePrototypeSession(
    withUpdatedAt({
      isLoggedIn: true,
      onboardingComplete: current.onboardingComplete,
      gradeLabel: current.gradeLabel,
    })
  );
};

export const completePrototypeOnboarding = async (
  gradeLabel: string
): Promise<PrototypeSession> =>
  savePrototypeSession(
    withUpdatedAt({
      isLoggedIn: true,
      onboardingComplete: true,
      gradeLabel,
    })
  );

export const resetPrototypeSession = async (): Promise<void> => {
  await AsyncStorage.removeItem(PROTOTYPE_SESSION_KEY);
};
